import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { ProductActions } from '../actions';
import { InventoryService } from '../inventory.service';
import { ProductService } from '../product.service';


@Injectable()
export class ProductEffects {
  private actions$ = inject(Actions);
  private productService = inject(ProductService);
  private inventoryService = inject(InventoryService);

  loadProduct$ = createEffect(() => this.actions$
    .pipe(
      ofType(ProductActions.loadProduct),
      exhaustMap(({ slugOrId }) => this.productService
        .get(slugOrId)
        .pipe(
          map(product => ProductActions.loadProductSuccess({ product })),
          catchError(error => of(ProductActions.loadProductFailure({ error }))),
        ),
      ),
    ));

  loadInventoryCount$ = createEffect(() => this.actions$
    .pipe(
      ofType(ProductActions.loadProductInventoryCount),
      exhaustMap(({ product, destination, dateOfStay }) => {
        if (product.rental) {
          return this.inventoryService
            .getAll({product, destination, dateOfStay})
            .pipe(
              map(inventoryItems => {
                const count = inventoryItems.length;

                return ProductActions.loadProductInventoryCountSuccess({ product, destination, count });
              }),
              catchError(error => of(ProductActions.loadProductInventoryCountFailure({ product, destination, error }))),
            );
        }

        return this.productService
          .getInventoryCount(product, destination)
          .pipe(
            map(count => ProductActions.loadProductInventoryCountSuccess({ product, destination, count })),
            catchError(error => of(ProductActions.loadProductInventoryCountFailure({ product, destination, error }))),
          );
      }),
  ));

  loadProducts$ = createEffect(() => this.actions$
    .pipe(
      ofType(ProductActions.loadProducts),
      exhaustMap(({ destination, storefront }) => this.productService
        .getAll(({ destination, storefront }))
        .pipe(
          map(products => ProductActions.loadProductsSuccess({
            products: (products || []).filter(p => p.destination_ids.includes(destination.id as number)),
          })),
          catchError(error => of(ProductActions.loadProductsFailure({ error }))),
        ),
      ),
    ));

  loadProductsByIds$ = createEffect(() => this.actions$
    .pipe(
      ofType(ProductActions.loadProductsByIds),
      exhaustMap(({ ids }) => forkJoin(ids.map(id => this.productService.get(+id)))
        .pipe(
          map(products => ProductActions.loadProductsByIdsSuccess({ products })),
          catchError(error => of(ProductActions.loadProductsByIdsFailure({ error }))),
        )
      ),
    ));
}

